import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import JobsiteApi from '../jobsite/jobsiteApi';
import { Jobsite, JobsiteAndProject } from '../jobsite/jobsiteType';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Trash2, AlertTriangle } from 'lucide-react';
import { formatAddress } from '../lib/global_function';
import { Label } from '@radix-ui/react-label';

const DeleteJobsitePage: React.FC = () => {
    const breadcrumbs = [
        { label: 'Project', path: '/main/project' },
        { label: 'Jobsite', path: '/main/project/jobsite' },
        { label: 'Delete', path: '/main/project/jobsite/delete' },
    ];

    const location = useLocation();
    const jobsiteData = location.state?.selectedJobsite as JobsiteAndProject;
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [jobsite, setJobsite] = useState<Partial<JobsiteAndProject>>(jobsiteData || {
        name: '',
        jobsite_description: '',
        address_1: '',
        address_2: '',
        city: '',
        state: '',
        zip: '',
        project_name: '',
        project_description: '',
        project_type: ''
    });

    useEffect(() => {
        if (!jobsiteData) {
            setErrorMessage('Jobsite not available, please select a jobsite by clicking Go To Jobsite button.');
        }
    }, [jobsiteData]);

    // Handle form submission to delete the jobsite
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsDeleting(true);
        setErrorMessage(null);

        try {
            await JobsiteApi.delete(String(jobsite.jobsite_id));
            setMessage('Jobsite deleted successfully');
            
            // Redirect after a short delay
            //setTimeout(() => {
            //    navigate('/main/project/jobsite');
            //}, 2000);
        } catch (error) {
            if (error instanceof Error) {
                setErrorMessage(error.message);
            } else {
                setErrorMessage('An unknown error occurred.');
            }
        } finally {
            setIsDeleting(false);
        }
    };

    const handleNavigateToJobsiteList = () => {
        navigate('/main/project/jobsite');
    };

    const handleCancel = () => {
        navigate('/main/project/jobsite');
    };

    return (
        <>
            <HorizontalMenu />
            <div className="container-safe py-6">
                <Breadcrumbs items={breadcrumbs} />

                <div className="mt-6">
                    <h1 className="text-2xl font-semibold text-foreground mb-6">Delete Jobsite</h1>

                    {/* Error Message - No jobsite data */}
                    {errorMessage && !jobsiteData && (
                        <div className="mb-6">
                            <Alert variant="error">
                                {errorMessage}
                            </Alert>
                            <div className="mt-4">
                                <Button size="medium" onClick={handleNavigateToJobsiteList}>
                                    Go To Jobsite
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Success Message */}
                    {message && (
                        <Alert variant="success" className="mb-6">
                            {message}
                        </Alert>
                    )}

                    {/* Delete Confirmation Form */}
                    {!message && jobsiteData && (
                        <div className="space-y-6">
                            {/* Warning Alert */}
                            <Alert variant="warning" >
                                
                                <div>
                                    <Label>Are you sure you want to delete this jobsite?</Label>&nbsp;
                                    <Label>This action cannot be undone. All data associated with this jobsite will be permanently removed.</Label>
                                </div>
                            </Alert>

                            {/* Project Context */}
{/** 
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-gray-700"><strong>Project:</strong></span>
                                    <Label>{jobsite.project_name}</Label>
                                    &nbsp;
                                    <span className="text-gray-700"><strong>Project Type:</strong></span>
                                    <Label>{jobsite.project_type}</Label>
                                </div>
                            </div>
*/}
                            {/* Jobsite Details Card */}
                            <div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">


                                <div className="space-y-3 text-sm">
                                    <div>
                                        <span className="font-semibold text-gray-700">Jobsite Name:</span>
                                        <span className="ml-2 text-foreground">{jobsite.name}</span>
                                    </div>

                                    <div>
                                        <span className="font-semibold text-gray-700">Description:</span>
                                        <span className="ml-2 text-foreground">
                                            {jobsite.jobsite_description || (
                                                <span className="text-gray-400 italic">No description</span>
                                            )}
                                        </span>
                                    </div>

                                    <div>
                                        <span className="font-semibold text-gray-700">Location:</span>
                                        <span className="ml-2 text-foreground">
                                            {formatAddress(
                                                jobsite?.address_1,
                                                jobsite?.address_2,
                                                jobsite?.city,
                                                jobsite?.state,
                                                jobsite?.zip
                                            ) || <span className="text-gray-400 italic">No address</span>}
                                        </span>
                                    </div>

                                    <div className="pt-3 border-t border-gray-200">
                                        <span className="font-semibold text-gray-700">Project Name:</span>
                                        <span className="ml-2 text-foreground">{jobsite.project_name}</span>
                                    </div>

                                    <div>
                                        <span className="font-semibold text-gray-700">Project Type:</span>
                                        <span className="ml-2 text-foreground">{jobsite.project_type}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Error Message - During deletion */}
                            {errorMessage && jobsiteData && (
                                <Alert variant="error">
                                    {errorMessage}
                                </Alert>
                            )}

                            {/* Action Buttons */}
                            <form onSubmit={handleSubmit}>
                                <div className="flex flex-wrap gap-3">
                                    <Button
                                        type="submit"
                                        size="medium"
                                        disabled={isDeleting}
                                    >
                                        <Trash2 size={18} />
                                        {isDeleting ? 'Deleting...' : 'Delete Jobsite'}
                                    </Button>
                                    <Button
                                        type="button"
                                        size="medium"
                                        variant="inverted"
                                        onClick={handleCancel}
                                        disabled={isDeleting}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default DeleteJobsitePage;